import React, { useCallback, useEffect, useMemo, useState } from "react";

const authHeaders = () => {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatCode = (code) => {
  const value = String(code || "");
  if (value.length === 4) return value;
  if (value.length === 6) return `${value.slice(0, 4)}.${value.slice(4)}`;
  if (value.length === 10) return `${value.slice(0, 4)}.${value.slice(4, 6)}-${value.slice(6)}`;
  return value;
};

const normalize = (value) => String(value || "").trim().toLowerCase();

const includesQuery = (node, query) => {
  if (!query) return true;
  return normalize(JSON.stringify(node)).includes(query);
};

function Stat({ label, value }) {
  return (
    <div style={S.stat}>
      <span style={S.statValue}>{Number(value || 0).toLocaleString()}</span>
      <span style={S.statLabel}>{label}</span>
    </div>
  );
}

function Badge({ children, tone = "slate" }) {
  const colors = {
    blue: { color: "#1d4ed8", background: "#dbeafe" },
    green: { color: "#166534", background: "#dcfce7" },
    amber: { color: "#92400e", background: "#fef3c7" },
    slate: { color: "#475569", background: "#f1f5f9" },
  };
  return <span style={{ ...S.badge, ...colors[tone] }}>{children}</span>;
}

function ChapterList({ chapters, selected, query, onQuery, onSelect }) {
  const filtered = chapters.filter((chapter) => {
    const needle = normalize(query).replace(/^제/, "").replace(/류$/, "");
    return !needle || chapter.chapter.includes(needle);
  });

  return (
    <aside style={S.chapterPanel}>
      <div style={S.panelTitle}>96개 활성 류</div>
      <input
        aria-label="류 검색"
        value={query}
        onChange={(event) => onQuery(event.target.value)}
        placeholder="류 번호 검색"
        style={S.searchInput}
      />
      <div style={S.chapterGrid}>
        {filtered.map((chapter) => (
          <button
            key={chapter.chapter}
            type="button"
            onClick={() => onSelect(chapter.chapter)}
            aria-pressed={selected === chapter.chapter}
            style={{
              ...S.chapterButton,
              ...(selected === chapter.chapter ? S.chapterButtonActive : null),
            }}
          >
            <strong>제{Number(chapter.chapter)}류</strong>
            <span>{chapter.hs4_count}호 · 축 {chapter.axis_count}</span>
          </button>
        ))}
      </div>
    </aside>
  );
}

function HeadingTree({ headings, query }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const visible = useMemo(
    () => headings.filter((heading) => includesQuery(heading, normalize(query))),
    [headings, query],
  );

  useEffect(() => {
    setExpanded(new Set());
  }, [headings]);

  const toggle = (hs4) => {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(hs4)) next.delete(hs4);
      else next.add(hs4);
      return next;
    });
  };

  return (
    <div>
      <div style={S.toolbar}>
        <span>{visible.length}개 호</span>
        <div style={S.toolbarActions}>
          <button type="button" style={S.textButton} onClick={() => setExpanded(new Set(visible.map((item) => item.hs4)))}>
            모두 펼치기
          </button>
          <button type="button" style={S.textButton} onClick={() => setExpanded(new Set())}>
            모두 접기
          </button>
        </div>
      </div>
      {visible.map((heading) => {
        const open = expanded.has(heading.hs4);
        return (
          <section key={heading.hs4} style={S.headingCard}>
            <button type="button" onClick={() => toggle(heading.hs4)} style={S.headingHeader} aria-expanded={open}>
              <span style={S.chevron}>{open ? "▾" : "▸"}</span>
              <code style={S.hs4}>{heading.hs4}</code>
              <span style={S.headingLabel}>{heading.label_ko || heading.label_en || "호 표제"}</span>
              <span style={S.headingCounts}>{heading.hs6_count} HS6 · {heading.hsk10_count} HSK10</span>
            </button>
            {open && (
              <div style={S.subheadingList}>
                {(heading.hs6 || []).map((subheading) => (
                  <div key={subheading.code} style={S.subheadingRow}>
                    <div style={S.subheadingHead}>
                      <code style={S.hs6}>{formatCode(subheading.code)}</code>
                      <span style={S.subheadingLabel}>{subheading.label_ko || subheading.label_en || "법정 소호"}</span>
                      {subheading.residual && <Badge tone="amber">잔여</Badge>}
                      <span style={S.leafCount}>{subheading.hsk10_count}개 세번</span>
                    </div>
                    {(subheading.hsk10 || []).length > 0 && (
                      <div style={S.leafGrid}>
                        {subheading.hsk10.map((leaf) => (
                          <div key={leaf.code} style={S.leaf}>
                            <code>{formatCode(leaf.code)}</code>
                            <span>{leaf.label_ko || ""}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        );
      })}
      {visible.length === 0 && <div style={S.empty}>검색 조건에 맞는 분류 경로가 없습니다.</div>}
    </div>
  );
}

function AxisList({ axes, query }) {
  const visible = axes.filter((axis) => includesQuery(axis, normalize(query)));
  return (
    <div style={S.cardGrid}>
      {visible.map((axis) => (
        <section key={`${axis.axis_id}-${axis.scope}`} style={S.infoCard}>
          <div style={S.infoCardHead}>
            <code style={S.axisId}>{axis.axis_id}</code>
            <Badge tone={axis.scope === "KR10" ? "blue" : "green"}>{axis.scope}</Badge>
          </div>
          <div style={S.codeWrap}>
            {(axis.applies_to || []).map((code) => <code key={code} style={S.codeChip}>{formatCode(code)}</code>)}
          </div>
          {axis.unit && <div style={S.metaLine}>단위: {axis.unit}</div>}
          {axis.condition_group && <div style={S.metaLine}>조건군: {JSON.stringify(axis.condition_group)}</div>}
          <div style={S.sourceRef}>{axis.source_ref || "원문 근거 연결"}</div>
        </section>
      ))}
      {visible.length === 0 && <div style={S.empty}>검색 조건에 맞는 갈림축이 없습니다.</div>}
    </div>
  );
}

function FactList({ facts, query }) {
  const visible = facts.filter((fact) => includesQuery(fact, normalize(query)));
  return (
    <div style={S.cardGrid}>
      {visible.map((fact) => (
        <section key={fact.key} style={S.infoCard}>
          <div style={S.infoCardHead}>
            <code style={S.axisId}>{fact.key}</code>
            <Badge>{fact.type || (fact.options ? "enum" : "fact")}</Badge>
          </div>
          <div style={S.prompt}>{fact.prompt_ko || "확인 질문"}</div>
          <div style={S.codeWrap}>
            {(fact.options || []).map((option) => (
              <span key={typeof option === "object" ? JSON.stringify(option) : String(option)} style={S.optionChip}>
                {typeof option === "object" ? option.label || option.value || JSON.stringify(option) : option}
              </span>
            ))}
          </div>
          {(fact.unit || fact.minimum !== undefined || fact.maximum !== undefined) && (
            <div style={S.metaLine}>
              {fact.unit ? `단위 ${fact.unit}` : "수치"}
              {fact.minimum !== undefined ? ` · 최소 ${fact.minimum}` : ""}
              {fact.maximum !== undefined ? ` · 최대 ${fact.maximum}` : ""}
            </div>
          )}
        </section>
      ))}
      {visible.length === 0 && <div style={S.empty}>검색 조건에 맞는 확인 질문이 없습니다.</div>}
    </div>
  );
}

function LegalBasis({ detail }) {
  return (
    <div style={S.legalWrap}>
      <section style={S.legalCard}>
        <div style={S.panelTitle}>법적 적용 순서</div>
        <div style={S.gateOrder}>{detail.legal_gate_order}</div>
      </section>
      <section style={S.legalCard}>
        <div style={S.panelTitle}>원문·활성 프레임 근거</div>
        <ol style={S.legalList}>
          {(detail.legal_basis || []).map((basis, index) => (
            <li key={`${index}-${JSON.stringify(basis)}`}>
              {typeof basis === "string" ? basis : basis.source || "법적 근거"}
              {basis?.counts && <span style={S.basisCounts}> · HS4 {basis.counts.headings} / HS6 {basis.counts.hs6} / HSK10 {basis.counts.hsk10}</span>}
            </li>
          ))}
        </ol>
      </section>
      <section style={S.securityCard}>
        <strong>관리자 읽기 전용</strong>
        <span>내부 키워드, holdout, 원천 파일경로, 해시와 감사필드는 API에서 제외됩니다.</span>
      </section>
    </div>
  );
}

export default function AdminDecisionTreeExplorer({ API_BASE_URL = "" }) {
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState("01");
  const [detail, setDetail] = useState(null);
  const [chapterQuery, setChapterQuery] = useState("");
  const [detailQuery, setDetailQuery] = useState("");
  const [tab, setTab] = useState("headings");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetch(`${API_BASE_URL}/api/onto/admin/decision-tree-explorer`, { headers: authHeaders() })
      .then((response) => {
        if (response.status === 401 || response.status === 403) throw new Error("관리자 인증이 필요합니다.");
        if (!response.ok) throw new Error("96류 요약을 불러오지 못했습니다.");
        return response.json();
      })
      .then((data) => {
        if (!active) return;
        setSummary(data);
        if (data.chapters?.length) {
          setSelected((current) => data.chapters.some((item) => item.chapter === current)
            ? current
            : data.chapters[0].chapter);
        }
      })
      .catch((requestError) => active && setError(requestError.message))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [API_BASE_URL]);

  const loadDetail = useCallback(() => {
    if (!selected) return () => {};
    const controller = new AbortController();
    setDetailLoading(true);
    setDetail(null);
    setDetailQuery("");
    fetch(`${API_BASE_URL}/api/onto/admin/decision-tree-explorer/${selected}`, {
      headers: authHeaders(),
      signal: controller.signal,
    })
      .then((response) => {
        if (response.status === 401 || response.status === 403) throw new Error("관리자 인증이 필요합니다.");
        if (!response.ok) throw new Error(`제${Number(selected)}류 결정트리를 불러오지 못했습니다.`);
        return response.json();
      })
      .then(setDetail)
      .catch((requestError) => {
        if (requestError.name !== "AbortError") setError(requestError.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setDetailLoading(false);
      });
    return () => controller.abort();
  }, [API_BASE_URL, selected]);

  useEffect(loadDetail, [loadDetail]);

  if (loading) return <div role="status" style={S.loading}>96류 결정트리를 불러오고 있습니다…</div>;
  if (error && !summary) return <div role="alert" style={S.error}>{error}</div>;

  const tabs = [
    { key: "headings", label: "분류 경로", count: detail?.summary?.hs4_count },
    { key: "axes", label: "갈림축", count: detail?.summary?.axis_count },
    { key: "facts", label: "확인 질문", count: detail?.summary?.fact_count },
    { key: "legal", label: "근거·보안" },
  ];

  return (
    <div style={S.page}>
      <header style={S.top}>
        <div>
          <div style={S.titleRow}>
            <h1 style={S.title}>96류 결정트리 탐색기</h1>
            <Badge tone="green">운영 reviewed</Badge>
            <Badge>읽기 전용</Badge>
          </div>
          <p style={S.subtitle}>갈림축과 법정 경로를 검수합니다. 코드 수는 규칙의 범위이며 자연어 자동분류 성공률을 뜻하지 않습니다. 일반 사용자 데이터와 내부 디버그 신호는 표시하지 않습니다.</p>
        </div>
        <div style={S.stats}>
          <Stat label="활성 류" value={summary?.totals?.active_chapters} />
          <Stat label="HS4" value={summary?.totals?.hs4} />
          <Stat label="HS6" value={summary?.totals?.hs6} />
          <Stat label="HSK10" value={summary?.totals?.hsk10} />
        </div>
      </header>

      {error && <div role="alert" style={S.inlineError}>{error}</div>}

      <div style={S.body}>
        <ChapterList
          chapters={summary?.chapters || []}
          selected={selected}
          query={chapterQuery}
          onQuery={setChapterQuery}
          onSelect={(chapter) => { setSelected(chapter); setTab("headings"); setError(""); }}
        />

        <main style={S.detailPanel}>
          {detailLoading ? (
            <div role="status" style={S.loading}>제{Number(selected)}류 상세를 불러오고 있습니다…</div>
          ) : !detail ? (
            <div role="alert" style={S.error}>{error || "결정트리 상세를 표시할 수 없습니다."}</div>
          ) : (
            <>
              <div style={S.detailTop}>
                <div>
                  <div style={S.titleRow}>
                    <h2 style={S.detailTitle}>제{Number(detail.chapter)}류</h2>
                    <Badge tone="green">{detail.summary.status}</Badge>
                    <Badge tone="blue">{detail.summary.source_kind === "specialized_evaluator" ? "특수 평가기" : "표준 평가기"}</Badge>
                  </div>
                  <div style={S.revision}>{detail.summary.revision || `${detail.summary.slice_count}개 운영 슬라이스`} · {detail.summary.review_status}</div>
                </div>
                <input
                  aria-label="현재 류 상세 검색"
                  value={detailQuery}
                  onChange={(event) => setDetailQuery(event.target.value)}
                  placeholder="코드·질문·갈림축 검색"
                  style={S.detailSearch}
                />
              </div>

              <div style={S.tabs}>
                {tabs.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setTab(item.key)}
                    style={{ ...S.tab, ...(tab === item.key ? S.tabActive : null) }}
                  >
                    {item.label}{item.count !== undefined ? ` ${item.count}` : ""}
                  </button>
                ))}
              </div>

              <div style={S.detailContent}>
                {tab === "headings" && <HeadingTree headings={detail.headings || []} query={detailQuery} />}
                {tab === "axes" && <AxisList axes={detail.axes || []} query={detailQuery} />}
                {tab === "facts" && <FactList facts={detail.facts || []} query={detailQuery} />}
                {tab === "legal" && <LegalBasis detail={detail} />}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

const S = {
  page: { height: "100%", minHeight: "calc(100vh - 56px)", display: "flex", flexDirection: "column", background: "#f8fafc", color: "#0f172a" },
  top: { padding: "18px 22px", background: "#fff", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20 },
  titleRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  title: { margin: 0, fontSize: 21, letterSpacing: "-0.02em" },
  detailTitle: { margin: 0, fontSize: 20 },
  subtitle: { margin: "5px 0 0", color: "#64748b", fontSize: 12.5 },
  stats: { display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" },
  stat: { minWidth: 76, border: "1px solid #e2e8f0", borderRadius: 9, padding: "8px 10px", background: "#fff", display: "flex", flexDirection: "column", alignItems: "flex-end" },
  statValue: { fontSize: 16, fontWeight: 750 },
  statLabel: { fontSize: 10.5, color: "#64748b" },
  badge: { display: "inline-flex", alignItems: "center", borderRadius: 999, padding: "2px 7px", fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap" },
  body: { flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: "260px minmax(0, 1fr)" },
  chapterPanel: { minHeight: 0, overflow: "auto", borderRight: "1px solid #e2e8f0", background: "#fff", padding: 14 },
  panelTitle: { fontSize: 13, fontWeight: 750, marginBottom: 9 },
  searchInput: { width: "100%", boxSizing: "border-box", border: "1px solid #cbd5e1", borderRadius: 7, padding: "8px 9px", marginBottom: 10, outline: "none", fontSize: 12 },
  chapterGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 6 },
  chapterButton: { border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", padding: "8px 7px", cursor: "pointer", textAlign: "left", display: "flex", flexDirection: "column", gap: 2, color: "#334155", fontSize: 11 },
  chapterButtonActive: { border: "1px solid #2563eb", background: "#eff6ff", color: "#1d4ed8", boxShadow: "0 0 0 1px #bfdbfe" },
  detailPanel: { minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", background: "#f8fafc" },
  detailTop: { padding: "14px 18px", background: "#fff", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14 },
  revision: { marginTop: 5, color: "#64748b", fontSize: 11.5, maxWidth: 760, wordBreak: "break-word" },
  detailSearch: { width: 270, maxWidth: "38%", border: "1px solid #cbd5e1", borderRadius: 7, padding: "8px 10px", outline: "none", fontSize: 12 },
  tabs: { display: "flex", gap: 4, padding: "9px 18px 0", background: "#fff", borderBottom: "1px solid #e2e8f0" },
  tab: { border: "none", borderBottom: "2px solid transparent", background: "transparent", color: "#64748b", padding: "8px 11px", fontSize: 12, fontWeight: 650, cursor: "pointer" },
  tabActive: { color: "#1d4ed8", borderBottom: "2px solid #2563eb" },
  detailContent: { flex: 1, minHeight: 0, overflow: "auto", padding: 16 },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", color: "#64748b", fontSize: 11.5, marginBottom: 8 },
  toolbarActions: { display: "flex", gap: 8 },
  textButton: { border: "none", background: "transparent", color: "#2563eb", cursor: "pointer", fontSize: 11.5, padding: 3 },
  headingCard: { border: "1px solid #e2e8f0", borderRadius: 9, background: "#fff", marginBottom: 8, overflow: "hidden" },
  headingHeader: { width: "100%", border: "none", background: "#fff", padding: "11px 12px", display: "grid", gridTemplateColumns: "18px 58px minmax(0, 1fr) auto", alignItems: "center", gap: 8, cursor: "pointer", textAlign: "left" },
  chevron: { color: "#64748b" },
  hs4: { fontSize: 13, fontWeight: 750, color: "#1d4ed8" },
  headingLabel: { fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  headingCounts: { color: "#64748b", fontSize: 10.5 },
  subheadingList: { borderTop: "1px solid #e2e8f0" },
  subheadingRow: { padding: "10px 14px 10px 38px", borderBottom: "1px solid #f1f5f9" },
  subheadingHead: { display: "flex", alignItems: "center", gap: 8 },
  hs6: { minWidth: 72, color: "#0f766e", fontWeight: 700 },
  subheadingLabel: { flex: 1, color: "#334155", fontSize: 11.5 },
  leafCount: { color: "#94a3b8", fontSize: 10.5 },
  leafGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 5, marginTop: 8 },
  leaf: { display: "grid", gridTemplateColumns: "105px minmax(0, 1fr)", gap: 8, borderRadius: 6, background: "#f8fafc", padding: "6px 8px", color: "#475569", fontSize: 10.5 },
  cardGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(330px, 1fr))", gap: 10 },
  infoCard: { border: "1px solid #e2e8f0", borderRadius: 9, background: "#fff", padding: 13, minWidth: 0 },
  infoCardHead: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 9 },
  axisId: { color: "#1d4ed8", fontWeight: 700, fontSize: 11.5, wordBreak: "break-all" },
  codeWrap: { display: "flex", flexWrap: "wrap", gap: 5 },
  codeChip: { background: "#f1f5f9", borderRadius: 5, padding: "3px 5px", fontSize: 10.5, color: "#334155" },
  optionChip: { background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 999, padding: "3px 7px", fontSize: 10.5, color: "#475569", wordBreak: "break-word" },
  prompt: { fontSize: 12.5, lineHeight: 1.55, marginBottom: 9 },
  metaLine: { marginTop: 8, color: "#64748b", fontSize: 10.5, lineHeight: 1.5, wordBreak: "break-word" },
  sourceRef: { marginTop: 9, paddingTop: 8, borderTop: "1px solid #f1f5f9", color: "#64748b", fontSize: 10.5, lineHeight: 1.5 },
  legalWrap: { display: "grid", gap: 10 },
  legalCard: { border: "1px solid #e2e8f0", borderRadius: 9, background: "#fff", padding: 15 },
  gateOrder: { fontFamily: "monospace", fontSize: 12.5, color: "#1d4ed8", background: "#eff6ff", borderRadius: 7, padding: 10 },
  legalList: { margin: 0, paddingLeft: 20, color: "#334155", fontSize: 12.5, lineHeight: 1.8 },
  basisCounts: { color: "#64748b", fontSize: 11 },
  securityCard: { border: "1px solid #bfdbfe", borderRadius: 9, background: "#eff6ff", color: "#1e40af", padding: 14, display: "flex", flexDirection: "column", gap: 4, fontSize: 12 },
  loading: { padding: 30, color: "#64748b", fontSize: 13 },
  error: { margin: 20, padding: 14, background: "#fef2f2", color: "#b91c1c", borderRadius: 8 },
  inlineError: { padding: "8px 18px", background: "#fef2f2", color: "#b91c1c", fontSize: 12, borderBottom: "1px solid #fecaca" },
  empty: { gridColumn: "1 / -1", padding: 26, textAlign: "center", color: "#94a3b8", fontSize: 12 },
};
